import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import api from '../../../utils/api';
import { useAuth } from '../../../App';
import {
  Archive, Download, Upload, CheckCircle2, Loader2,
  FileDown, RotateCcw, AlertTriangle, RefreshCw, Filter, X, ArrowUpDown, ArrowUp, ArrowDown,
  ChevronRight, ChevronDown, Sparkles, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const METHOD = {
  sepa: { label: 'SEPA', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  f24: { label: 'F24', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  pagopa: { label: 'PagoPA', cls: 'bg-teal-100 text-teal-700 border-teal-200' },
  mixed: { label: 'Misto', cls: 'bg-slate-200 text-slate-700 border-slate-300' },
  other: { label: 'Altro', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const eur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

const STATUS_LABEL = {
  paid: { label: 'Pagato', cls: 'bg-emerald-100 text-emerald-700' },
  ready_to_pay: { label: 'Da pagare', cls: 'bg-amber-100 text-amber-700' },
};

// Dettaglio di un lotto IDS raggruppato per Comune (immobili / periodo / importo).
function IdsBreakdown({ data, loading }) {
  const [openComune, setOpenComune] = useState({});
  if (loading) return <div className="p-4 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-slate-400" /></div>;
  if (!data || !(data.comuni || []).length) return <div className="p-4 text-sm text-slate-400 text-center">Nessun dettaglio disponibile per questo lotto</div>;
  return (
    <div className="bg-slate-50 border-t border-b max-h-[420px] overflow-y-auto" data-testid={`ids-breakdown-${data.batch_id}`}>
      <table className="w-full text-xs">
        <thead className="bg-slate-100 text-slate-600 sticky top-0">
          <tr>
            <th className="w-8 p-2"></th>
            <th className="text-left px-2 py-2 font-medium">Comune</th>
            <th className="text-left px-2 py-2 font-medium">Beneficiario / IBAN</th>
            <th className="text-right px-2 py-2 font-medium">N.</th>
            <th className="text-right px-2 py-2 font-medium pr-4">Totale</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {data.comuni.map((c, i) => {
            const open = openComune[c.comune_name];
            return (
              <React.Fragment key={i}>
                <tr className={`cursor-pointer ${open ? 'bg-blue-50' : 'hover:bg-white'}`} onClick={() => setOpenComune(p => ({ ...p, [c.comune_name]: !open }))} data-testid={`ids-comune-row-${c.comune_name}`}>
                  <td className="p-2">{open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}</td>
                  <td className="px-2 py-2 font-medium text-slate-800">{c.comune_name}</td>
                  <td className="px-2 py-2 text-slate-500">
                    {c.beneficiary_name || '-'}
                    {c.iban && <div className="font-mono text-[10px] text-slate-400">{c.iban}</div>}
                  </td>
                  <td className="px-2 py-2 text-right">{c.count}</td>
                  <td className="px-2 py-2 text-right font-semibold pr-4">{eur(c.total)}</td>
                </tr>
                {open && (
                  <tr><td colSpan={5} className="p-0"><div className="bg-white border-t border-slate-200">
                    <table className="w-full text-[11px]">
                      <thead><tr className="text-slate-400">
                        <th className="pl-10 pr-2 py-1.5 text-left font-medium">Immobile</th>
                        <th className="px-2 py-1.5 text-left font-medium">Periodo</th>
                        <th className="px-2 py-1.5 text-center font-medium">Stato</th>
                        <th className="px-2 py-1.5 text-right font-medium pr-4">Importo</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-100">
                        {c.reports.map((r, j) => {
                          const st = STATUS_LABEL[r.status] || STATUS_LABEL.ready_to_pay;
                          return (
                            <tr key={j} className="hover:bg-slate-50">
                              <td className="pl-10 pr-2 py-1.5 text-slate-700">{r.property_name}</td>
                              <td className="px-2 py-1.5 text-slate-600">{r.period}</td>
                              <td className="px-2 py-1.5 text-center"><span className={`px-1.5 py-0.5 rounded text-[10px] ${st.cls}`}>{st.label}</span></td>
                              <td className="px-2 py-1.5 text-right font-medium text-slate-800 pr-4">{eur(r.amount)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div></td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Tab "Tassa di Soggiorno (IDS)" dell'Archivio Pagamenti.
// Stesso flusso di Proprietari/Fatture: carichi la contabile sul lotto e i report
// collegati vengono marcati pagati in automatico. Con filtri di ricerca + ordinamento colonne.
export default function TouristTaxArchiveTab({ year }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingBatch, setUploadingBatch] = useState(null);
  const [aiMatching, setAiMatching] = useState(null);
  const [matchResult, setMatchResult] = useState(null);

  // Espansione riga lotto -> dettaglio per Comune (fetch lazy)
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [breakdowns, setBreakdowns] = useState({});
  const [loadingBreakdown, setLoadingBreakdown] = useState({});

  // Filtri
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');   // all | ready | paid
  const [methodFilter, setMethodFilter] = useState('all');   // all | sepa | f24 | pagopa | mixed
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');

  // Ordinamento
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/tourist-tax/xml-batches');
      setBatches(r.data || []);
    } catch { toast.error('Errore caricamento lotti IDS'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  const fmtDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  const hasFilters = search || statusFilter !== 'all' || methodFilter !== 'all' || dateFrom || dateTo || amountMin || amountMax;
  const clearFilters = () => {
    setSearch(''); setStatusFilter('all'); setMethodFilter('all');
    setDateFrom(''); setDateTo(''); setAmountMin(''); setAmountMax('');
  };

  const filtered = useMemo(() => {
    return (batches || []).filter(b => {
      if (year && b.created_at) {
        try { if (new Date(b.created_at).getFullYear() !== Number(year)) return false; } catch { /* noop */ }
      }
      if (search && !(b.batch_id || '').toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== 'all' && (b.status || 'ready') !== statusFilter) return false;
      if (methodFilter !== 'all' && (b.payment_method || 'sepa') !== methodFilter) return false;
      if (dateFrom && b.created_at && new Date(b.created_at) < new Date(dateFrom)) return false;
      if (dateTo && b.created_at && new Date(b.created_at) > new Date(`${dateTo}T23:59:59`)) return false;
      const amt = Number(b.total_amount) || 0;
      if (amountMin !== '' && amt < parseFloat(amountMin)) return false;
      if (amountMax !== '' && amt > parseFloat(amountMax)) return false;
      return true;
    });
  }, [batches, year, search, statusFilter, methodFilter, dateFrom, dateTo, amountMin, amountMax]);

  const sorted = useMemo(() => {
    const val = (b, f) => {
      if (f === 'created_at' || f === 'paid_at') return b[f] ? new Date(b[f]).getTime() : 0;
      if (f === 'total_amount' || f === 'nb_txs') return Number(b[f]) || 0;
      if (f === 'status') return (b.status || 'ready') === 'paid' ? 1 : 0;
      if (f === 'payment_method') return b.payment_method || 'sepa';
      return b[f] || '';
    };
    return [...filtered].sort((a, b) => {
      const va = val(a, sortField), vb = val(b, sortField);
      let cmp = typeof va === 'string' ? va.localeCompare(vb) : (va < vb ? -1 : va > vb ? 1 : 0);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortField, sortDir]);

  const paidTotal = filtered.filter(b => b.status === 'paid').reduce((s, b) => s + (Number(b.total_amount) || 0), 0);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
  };
  const renderSortTh = (field, label, align = 'left') => (
    <th className={`px-3 py-2 cursor-pointer select-none hover:bg-slate-100 text-${align}`} onClick={() => toggleSort(field)} data-testid={`ids-sort-${field}`}>
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {sortField === field ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
      </span>
    </th>
  );

  const downloadXml = async (batchId) => {
    try {
      const r = await api.get(`/tourist-tax/xml-batches/${batchId}`);
      if (!r.data.xml_content) { toast.error('Questa voce non ha un XML (metodo non SEPA)'); return; }
      const blob = new Blob([r.data.xml_content], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `IDS_${batchId}.xml`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Errore download XML'); }
  };

  const downloadBlob = async (url, filename) => {
    try {
      const r = await api.get(url, { responseType: 'blob' });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = u; a.download = filename; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore download'); }
  };

  const uploadReceipt = async (batch, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setUploadingBatch(batch.batch_id);
    try {
      const r = await api.post(`/tourist-tax/xml-batches/${batch.batch_id}/upload-receipt`, fd);
      toast.success(`Contabile caricata: ${r.data.report_ids_updated || 0} report marcati pagati`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore upload contabile'); }
    finally { setUploadingBatch(null); }
  };

  const aiMatchReceipts = async (batch, fileList) => {
    if (!fileList || !fileList.length) return;
    const fd = new FormData();
    Array.from(fileList).forEach(f => fd.append('files', f));
    setAiMatching(batch.batch_id);
    try {
      const r = await api.post(`/tourist-tax/xml-batches/${batch.batch_id}/ai-match-receipts`, fd, { timeout: 180000 });
      setMatchResult({ batchId: batch.batch_id, ...r.data });
      const applied = r.data.applied || 0;
      if (applied > 0) toast.success(`${applied}/${r.data.files_processed} contabili abbinate automaticamente`);
      else toast.warning('Nessun abbinamento sicuro: rivedi il risultato');
      load();
      if (breakdowns[batch.batch_id]) {
        try { const b = await api.get(`/tourist-tax/xml-batches/${batch.batch_id}/breakdown`); setBreakdowns(p => ({ ...p, [batch.batch_id]: b.data })); } catch { /* noop */ }
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore matching AI'); }
    finally { setAiMatching(null); }
  };

  const markPaid = async (batch) => {
    const ref = window.prompt('Riferimento pagamento (opzionale: CRO / F24 / PagoPA):', '');
    if (ref === null) return;
    try {
      const r = await api.post(`/tourist-tax/xml-batches/${batch.batch_id}/mark-paid`, { payment_reference: ref.trim() || null });
      toast.success(`Voce marcata pagata (${r.data.report_ids_updated || 0} report)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const unmark = async (batch) => {
    if (!window.confirm(`Annullare lo stato 'pagato' del lotto ${batch.batch_id}? La contabile sarà rimossa e i report torneranno a "da pagare".`)) return;
    try { await api.post(`/tourist-tax/xml-batches/${batch.batch_id}/unmark-paid`); toast.success('Stato annullato'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const deleteBatch = async (batch) => {
    if (!window.confirm(`Eliminare definitivamente il lotto ${batch.batch_id}?\nI ${batch.nb_txs} report collegati torneranno disponibili per una nuova generazione.\nL'operazione non è reversibile.`)) return;
    try {
      const r = await api.delete(`/tourist-tax/xml-batches/${batch.batch_id}`);
      toast.success(`Lotto eliminato (${r.data.reports_released || 0} report scollegati)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore eliminazione lotto'); }
  };

  const toggleBatch = useCallback(async (batchId) => {
    if (expandedBatch === batchId) { setExpandedBatch(null); return; }
    setExpandedBatch(batchId);
    if (!breakdowns[batchId]) {
      setLoadingBreakdown(p => ({ ...p, [batchId]: true }));
      try {
        const r = await api.get(`/tourist-tax/xml-batches/${batchId}/breakdown`);
        setBreakdowns(p => ({ ...p, [batchId]: r.data }));
      } catch { toast.error('Errore caricamento dettaglio lotto'); }
      finally { setLoadingBreakdown(p => ({ ...p, [batchId]: false })); }
    }
  }, [expandedBatch, breakdowns]);

  return (
    <div className="space-y-3" data-testid="ids-archive-tab">
      <Card className="p-3 bg-emerald-50 border-emerald-200">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-emerald-900">
            <span className="text-sm">Totale IDS pagato {year}:</span>
            <span className="text-xl font-bold">{fmtEur(paidTotal)}</span>
            <span className="text-xs text-emerald-700">({sorted.length} voci)</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant={showFilters ? 'default' : 'outline'} onClick={() => setShowFilters(s => !s)} data-testid="ids-toggle-filters">
              <Filter className="h-4 w-4 mr-1.5" /> Filtri{hasFilters ? ' •' : ''}
            </Button>
            <Button size="sm" variant="outline" onClick={load} data-testid="ids-archive-refresh">
              <RefreshCw className="h-4 w-4 mr-1.5" /> Aggiorna
            </Button>
          </div>
        </div>
      </Card>

      {showFilters && (
        <Card className="p-4" data-testid="ids-filters-panel">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Cerca (Batch ID)</label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="IDS-..." data-testid="ids-filter-search" />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Stato</label>
              <select className="w-full text-sm border rounded-md px-2 py-2 bg-white" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="ids-filter-status">
                <option value="all">Tutti</option>
                <option value="ready">Da pagare</option>
                <option value="paid">Pagato</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Metodo</label>
              <select className="w-full text-sm border rounded-md px-2 py-2 bg-white" value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)} data-testid="ids-filter-method">
                <option value="all">Tutti</option>
                <option value="sepa">SEPA</option>
                <option value="f24">F24</option>
                <option value="pagopa">PagoPA</option>
                <option value="mixed">Misto</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Importo (min / max)</label>
              <div className="flex gap-2">
                <Input type="number" value={amountMin} onChange={(e) => setAmountMin(e.target.value)} placeholder="min" data-testid="ids-filter-amount-min" />
                <Input type="number" value={amountMax} onChange={(e) => setAmountMax(e.target.value)} placeholder="max" data-testid="ids-filter-amount-max" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Data creazione da</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="ids-filter-date-from" />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Data creazione a</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="ids-filter-date-to" />
            </div>
            {hasFilters && (
              <div className="flex items-end">
                <Button size="sm" variant="outline" onClick={clearFilters} data-testid="ids-clear-filters">
                  <X className="h-4 w-4 mr-1" /> Azzera filtri
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
      ) : sorted.length === 0 ? (
        <Card className="p-8 text-center text-slate-400"><Archive className="h-8 w-8 mx-auto mb-2" /><p>{hasFilters ? 'Nessuna voce per i filtri selezionati' : `Nessun lotto IDS per ${year}`}</p></Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ids-batches-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="w-8 px-2 py-2"></th>
                {renderSortTh('created_at', 'Data Creazione')}
                <th className="text-left px-3 py-2">Batch ID</th>
                {renderSortTh('payment_method', 'Metodo')}
                {renderSortTh('nb_txs', 'Tx', 'right')}
                {renderSortTh('total_amount', 'Totale', 'right')}
                {renderSortTh('status', 'Stato', 'center')}
                {renderSortTh('paid_at', 'Pagato il')}
                <th className="text-right px-3 py-2 min-w-[300px]">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(b => {
                const isPaid = b.status === 'paid';
                const hasReceipt = !!b.receipt_filename;
                const meth = METHOD[b.payment_method || 'sepa'] || METHOD.other;
                const isSepa = (b.payment_method || 'sepa') === 'sepa';
                return (
                  <React.Fragment key={b.batch_id}>
                  <tr className={`border-b hover:bg-slate-50 cursor-pointer ${isPaid ? 'bg-emerald-50/30' : ''} ${expandedBatch === b.batch_id ? 'bg-blue-50' : ''}`} onClick={() => toggleBatch(b.batch_id)} data-testid={`ids-batch-row-${b.batch_id}`}>
                    <td className="px-2 py-2 text-slate-400" data-testid={`ids-expand-${b.batch_id}`}>
                      {expandedBatch === b.batch_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{b.batch_id}</td>
                    <td className="px-3 py-2"><Badge className={meth.cls}>{meth.label}</Badge></td>
                    <td className="px-3 py-2 text-right">{b.nb_txs}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtEur(b.total_amount)}</td>
                    <td className="px-3 py-2 text-center">
                      {isPaid ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200"><CheckCircle2 className="h-3 w-3 mr-1" /> Pagato</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200"><AlertTriangle className="h-3 w-3 mr-1" /> Da pagare</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                      {isPaid ? fmtDate(b.paid_at) : '-'}
                      {hasReceipt && <div className="text-[10px] text-slate-400">📎 {b.receipt_filename}</div>}
                    </td>
                    <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {isSepa && (
                        <Button size="sm" variant="outline" onClick={() => downloadXml(b.batch_id)} title="Scarica XML SEPA" data-testid={`ids-download-xml-${b.batch_id}`}>
                          <Download className="h-3 w-3 mr-1" /> XML
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => downloadBlob(`/tourist-tax/xml-batches/${b.batch_id}/distinte-zip`, `distinte_IDS_${b.batch_id}.zip`)} title="Scarica ZIP distinte per Comune" data-testid={`ids-download-distinte-${b.batch_id}`}>
                        <FileDown className="h-3 w-3 mr-1" /> Distinte
                      </Button>
                      <label className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100 cursor-pointer" title="Carica una o più contabili: l'AI legge Comune e importo dentro al PDF e le abbina al report giusto">
                        {aiMatching === b.batch_id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                        Abbina AI
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          accept=".pdf,.png,.jpg,.jpeg,.webp"
                          data-testid={`ids-ai-match-${b.batch_id}`}
                          onChange={(e) => { aiMatchReceipts(b, e.target.files); e.target.value = ''; }}
                          disabled={aiMatching === b.batch_id}
                        />
                      </label>
                      {hasReceipt && (
                        <Button size="sm" variant="outline" onClick={() => downloadBlob(`/tourist-tax/xml-batches/${b.batch_id}/download-receipt`, b.receipt_filename || `ricevuta_${b.batch_id}.pdf`)} title="Scarica contabile" data-testid={`ids-download-receipt-${b.batch_id}`}>
                          <Archive className="h-3 w-3 mr-1" /> Contabile
                        </Button>
                      )}
                      {!isPaid && (
                        <>
                          <label className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border bg-white hover:bg-slate-50 cursor-pointer" title="Carica contabile (PDF/immagine) → pagato">
                            {uploadingBatch === b.batch_id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
                            Carica contabile
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.png,.jpg,.jpeg,.webp"
                              data-testid={`ids-upload-receipt-${b.batch_id}`}
                              onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) uploadReceipt(b, f); e.target.value = ''; }}
                              disabled={uploadingBatch === b.batch_id}
                            />
                          </label>
                          <Button size="sm" onClick={() => markPaid(b)} className="bg-emerald-600 hover:bg-emerald-700 text-white" title="Marca pagato senza contabile (es. F24)" data-testid={`ids-mark-paid-${b.batch_id}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Pagato
                          </Button>
                        </>
                      )}
                      {isAdmin && isPaid && (
                        <Button size="sm" variant="outline" onClick={() => unmark(b)} title="Annulla stato pagato" data-testid={`ids-unmark-paid-${b.batch_id}`}>
                          <RotateCcw className="h-3 w-3 mr-1" /> Annulla
                        </Button>
                      )}
                      {isAdmin && !isPaid && (
                        <Button size="sm" variant="outline" onClick={() => deleteBatch(b)}
                          className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          title="Elimina lotto (i report tornano disponibili)" data-testid={`ids-delete-batch-${b.batch_id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                  {expandedBatch === b.batch_id && (
                    <tr data-testid={`ids-batch-detail-${b.batch_id}`}>
                      <td colSpan={9} className="p-0">
                        <IdsBreakdown data={breakdowns[b.batch_id]} loading={loadingBreakdown[b.batch_id]} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!matchResult} onOpenChange={(o) => { if (!o) setMatchResult(null); }}>
        <DialogContent className="max-w-2xl" data-testid="ids-ai-match-result">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" /> Risultato abbinamento AI</DialogTitle>
          </DialogHeader>
          {matchResult && (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto text-sm">
              <p className="text-slate-600">
                <b>{matchResult.applied}</b> / {matchResult.files_processed} contabili abbinate e associate automaticamente.
              </p>
              {(matchResult.results || []).map((r, i) => (
                <div key={i} className={`p-2 rounded border ${r.applied ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid={`ids-ai-result-${i}`}>
                  <div className="font-medium flex items-center gap-2">
                    {r.applied ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                    <span className="truncate">{r.filename}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    Rilevato: <b>{r.detected?.comune || '—'}</b> · {fmtEur(r.detected?.importo)} · {r.detected?.data || '—'}
                  </div>
                  <div className="text-xs text-slate-500">
                    {r.applied
                      ? `Associata a ${r.matched_report_ids.length} report (${r.match_type}, confidenza ${r.confidence})`
                      : `Non associata automaticamente — ${r.reason || 'nessun abbinamento sicuro'}`}
                  </div>
                </div>
              ))}
              <p className="text-xs text-slate-400">Le contabili non abbinate vanno caricate manualmente sul report corretto.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
